import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { AlertCircle, Loader2, Filter } from 'lucide-react';
import clsx from 'clsx';
import { useSpendingByCategoryOverTime } from '../hooks/useDashboardSpending';
import { getCurrentUserId } from '../../../lib/supabase';
import { useRegion } from '../../../contexts/RegionContext';
import { getCategoryIconComponent } from '../../../utils/icon-utils';
import { CategoryType } from '../../../types/category.types';
import {
  DateRangeSelector,
  type DateRange,
  getPresetLabel,
} from './DateRangeSelector';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

interface SpendingTrendsByCategoryCardProps {
  dateFrom?: string;
  dateTo?: string;
  onDateRangeChange?: (range: { from: string; to: string }) => void;
  grouping?: 'parent' | 'category';
  showExcluded?: boolean;
  className?: string;
}

const FALLBACK_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#8B5CF6', // purple
  '#F97316', // orange
  '#EC4899', // pink
];

function TrendTooltip({
  active,
  payload,
  label,
  formatter,
  topCategories,
}: {
  active?: boolean;
  payload?: any[];
  label?: string;
  formatter: (value: number) => string;
  topCategories: Array<{ categoryId: string | null; categoryName: string; categoryColor?: string }>;
}) {
  if (!active || !payload || payload.length === 0) return null;

  // Group entries by category (strip _actual/_projected suffix)
  // For each category, prefer actual value if available, otherwise use projected
  const categoryMap = new Map<string, { entry: any; isProjected: boolean }>();
  
  for (const entry of payload) {
    if (!entry.dataKey || entry.value == null) continue;
    
    // Extract category key by removing _actual or _projected suffix
    const categoryKey = entry.dataKey.replace(/_actual$|_projected$/, '');
    const isProjected = entry.dataKey.endsWith('_projected');
    
    // Only include if this category is in topCategories
    const isTopCategory = topCategories.some(
      (cat) => (cat.categoryId ?? 'uncategorised') === categoryKey
    );
    
    if (!isTopCategory) continue;
    
    // Prefer actual over projected if both exist
    if (!categoryMap.has(categoryKey) || (!isProjected && categoryMap.get(categoryKey)?.isProjected)) {
      categoryMap.set(categoryKey, { entry, isProjected });
    }
  }

  // Convert to array and sort by value descending - show all categories, not just top 5
  const displayEntries = Array.from(categoryMap.entries())
    .map(([categoryKey, { entry, isProjected }]) => {
      const category = topCategories.find(
        (cat) => (cat.categoryId ?? 'uncategorised') === categoryKey
      );
      return {
        categoryKey,
        categoryName: category?.categoryName || (categoryKey === 'uncategorised' ? 'Uncategorised' : categoryKey),
        categoryColor: category?.categoryColor,
        value: entry.value || 0,
        isProjected,
      };
    })
    .filter((item) => item.value > 0 || item.categoryKey === 'uncategorised')
    .sort((a, b) => b.value - a.value);

  // Calculate total from all display entries
  const total = displayEntries.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-lg dark:border-gray-700 dark:bg-gray-800">
      <p className="font-medium text-gray-900 dark:text-gray-50 mb-2">{label}</p>
      {displayEntries.map((item, index) => {
        const color = item.categoryColor || FALLBACK_COLORS[index % FALLBACK_COLORS.length];

        return (
          <div key={item.categoryKey} className="flex items-center justify-between space-x-4 mb-1 last:mb-0">
            <div className="flex items-center space-x-2">
              <span
                className="inline-flex h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: color }}
                aria-hidden="true"
              />
              <span className="text-gray-600 dark:text-gray-300">
                {item.categoryName}
                {item.isProjected && (
                  <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">(Projected)</span>
                )}
              </span>
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-50">
              {formatter(item.value)}
            </span>
          </div>
        );
      })}
      {total > 0 && (
        <>
          <div className="border-t border-gray-200 dark:border-gray-700 my-2" />
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-50">Total</span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-50">
              {formatter(total)}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

type CategoryLimit = 'top5' | 'top10' | 'all';

export function SpendingTrendsByCategoryCard({
  dateFrom,
  dateTo,
  onDateRangeChange,
  grouping = 'parent',
  showExcluded = false,
  className,
}: SpendingTrendsByCategoryCardProps) {
  const [userId, setUserId] = React.useState<string | null>(null);
  const [categoryLimit, setCategoryLimit] = React.useState<CategoryLimit>('top5');
  const [selectedCategories, setSelectedCategories] = React.useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = React.useState(false);
  const region = useRegion();
  const formatCurrency = React.useMemo(() => {
    if (!region?.formatCurrency) {
      return (amount: number) => {
        return new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      };
    }
    return region.formatCurrency;
  }, [region]);

  // Default to last 3 months if no dates provided
  const initialRange = React.useMemo(() => {
    if (dateFrom && dateTo) {
      return { from: dateFrom, to: dateTo };
    }

    const now = new Date();
    const lastMonthEnd = endOfMonth(subMonths(now, 1));
    return {
      from: format(startOfMonth(subMonths(now, 3)), 'yyyy-MM-dd'),
      to: format(lastMonthEnd, 'yyyy-MM-dd'),
    };
  }, [dateFrom, dateTo]);

  const [dateRange, setDateRange] = React.useState<DateRange>(initialRange);

  React.useEffect(() => {
    getCurrentUserId().then(setUserId);
  }, []);

  // Only sync with parent props if they're explicitly provided
  // Otherwise, maintain independent date range state
  React.useEffect(() => {
    if (dateFrom && dateTo) {
      setDateRange({ from: dateFrom, to: dateTo });
    }
  }, [dateFrom, dateTo]);

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    onDateRangeChange?.(range);
  };

  // Ensure dateRange is always valid before using it
  const validDateRange = React.useMemo(() => {
    if (!dateRange || !dateRange.from || !dateRange.to) {
      return initialRange;
    }
    return dateRange;
  }, [dateRange, initialRange]);

  const periodLabel = React.useMemo(() => getPresetLabel(validDateRange), [validDateRange]);

  const {
    chartData,
    topCategories,
    isLoading,
    isError,
    error,
    isFetching,
    transactionCount,
  } = useSpendingByCategoryOverTime({
    userId: userId ?? '',
    dateFrom: validDateRange.from,
    dateTo: validDateRange.to,
    grouping,
    includeExcluded: showExcluded,
  });

  const hasData = chartData.length > 0 && topCategories.length > 0;

  // Filter categories based on limit selection
  const filteredCategories = React.useMemo(() => {
    if (!topCategories.length) return [];
    
    // Sort by total spending across all periods
    const categoryTotals = topCategories.map((category) => {
      const categoryKey = category.categoryId ?? 'uncategorised';
      const total = chartData.reduce((sum, point) => {
        return sum + ((point[categoryKey] as number) || 0);
      }, 0);
      return { category, total };
    });
    
    categoryTotals.sort((a, b) => b.total - a.total);
    
    let limit = categoryTotals.length;
    if (categoryLimit === 'top5') {
      limit = Math.min(5, categoryTotals.length);
    } else if (categoryLimit === 'top10') {
      limit = Math.min(10, categoryTotals.length);
    }
    
    return categoryTotals.slice(0, limit).map((item) => item.category);
  }, [topCategories, chartData, categoryLimit]);

  // Track previous categoryLimit to detect changes
  const prevCategoryLimitRef = React.useRef<CategoryLimit>(categoryLimit);

  // Initialize selected categories when filtered categories change
  React.useEffect(() => {
    if (filteredCategories.length > 0) {
      const filteredIds = new Set(
        filteredCategories.map((cat) => cat.categoryId ?? 'uncategorised')
      );
      
      // If categoryLimit changed, select all filtered categories by default
      if (prevCategoryLimitRef.current !== categoryLimit) {
        setSelectedCategories(filteredIds);
        prevCategoryLimitRef.current = categoryLimit;
      } else if (selectedCategories.size === 0) {
        // Initialize with all categories selected if none are selected
        setSelectedCategories(filteredIds);
      } else {
        // Otherwise, update selected categories to only include those in filtered list
        const updated = new Set<string>();
        selectedCategories.forEach((id) => {
          if (filteredIds.has(id)) {
            updated.add(id);
          }
        });
        // If no categories are selected after filtering, select all
        if (updated.size === 0) {
          setSelectedCategories(filteredIds);
        } else if (updated.size !== selectedCategories.size) {
          setSelectedCategories(updated);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCategories, categoryLimit]);

  // Get categories to display (filtered by checkboxes)
  const displayCategories = React.useMemo(() => {
    return filteredCategories.filter(
      (cat) => selectedCategories.has(cat.categoryId ?? 'uncategorised')
    );
  }, [filteredCategories, selectedCategories]);

  const handleCategoryToggle = (categoryId: string | null) => {
    const id = categoryId ?? 'uncategorised';
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Find the index where projected data starts
  const projectedStartIndex = React.useMemo(() => {
    const index = chartData.findIndex((point) => point.isProjected);
    return index >= 0 ? index : chartData.length;
  }, [chartData]);

  return (
    <div className={clsx('card h-full', className)}>
      <div className="card-header">
        <div>
          <h2 className="card-title">Spending trends by category</h2>
          <p className="card-description">
            Daily, weekly & monthly patterns
            <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700/50 dark:text-gray-200">
              {periodLabel}
            </span>
            {transactionCount !== undefined && (
              <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700/50 dark:text-gray-200">
                {transactionCount.toLocaleString()} transactions
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-col gap-4">
          {/* Mobile Filter Toggle Button - Only visible on mobile */}
          <div className="md:hidden">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={clsx(
                'flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors duration-200',
                showFilters
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 border border-primary-200 dark:border-primary-800'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
              )}
              aria-expanded={showFilters}
              aria-controls="trends-filters"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
            </button>
          </div>

          {/* Filter Controls - Hidden on mobile, visible on desktop */}
          <div 
            id="trends-filters"
            className="hidden md:flex flex-wrap items-center justify-start gap-3"
          >
            <DateRangeSelector
              value={validDateRange}
              onChange={handleDateRangeChange}
              className="h-11"
            />
            {isFetching && (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label="Updating trends data" />
            )}
          </div>

          {/* Category Limit Radio Buttons - Desktop */}
          {hasData && (
            <div className="hidden md:flex items-center gap-4 mt-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Show:</span>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="category-limit"
                    value="top5"
                    checked={categoryLimit === 'top5'}
                    onChange={(e) => setCategoryLimit(e.target.value as CategoryLimit)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Top 5 Categories</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="category-limit"
                    value="top10"
                    checked={categoryLimit === 'top10'}
                    onChange={(e) => setCategoryLimit(e.target.value as CategoryLimit)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Top 10 Categories</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="category-limit"
                    value="all"
                    checked={categoryLimit === 'all'}
                    onChange={(e) => setCategoryLimit(e.target.value as CategoryLimit)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">All Categories</span>
                </label>
              </div>
            </div>
          )}

          {/* Mobile Filter Panel - Only shown when expanded on mobile */}
          {showFilters && (
            <div className="md:hidden pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col gap-4">
                <DateRangeSelector
                  value={validDateRange}
                  onChange={handleDateRangeChange}
                  className="h-11"
                />
                {isFetching && (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label="Updating trends data" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Updating...</span>
                  </div>
                )}
                {/* Category Limit Radio Buttons - Mobile */}
                {hasData && (
                  <div className="flex flex-col gap-3">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Show:</span>
                    <div className="flex flex-col gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="category-limit-mobile"
                          value="top5"
                          checked={categoryLimit === 'top5'}
                          onChange={(e) => setCategoryLimit(e.target.value as CategoryLimit)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Top 5 Categories</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="category-limit-mobile"
                          value="top10"
                          checked={categoryLimit === 'top10'}
                          onChange={(e) => setCategoryLimit(e.target.value as CategoryLimit)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Top 10 Categories</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="category-limit-mobile"
                          value="all"
                          checked={categoryLimit === 'all'}
                          onChange={(e) => setCategoryLimit(e.target.value as CategoryLimit)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">All Categories</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card-content">
        {isLoading || userId === null ? (
          <div className="flex h-80 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" aria-label="Loading trends data" />
          </div>
        ) : isError ? (
          <div className="flex h-80 flex-col items-center justify-center space-y-2 text-center">
            <AlertCircle className="h-8 w-8 text-red-500" aria-hidden="true" />
            <p className="text-sm text-red-600 dark:text-red-400">
              {error instanceof Error ? error.message : 'Unable to load spending trends.'}
            </p>
          </div>
        ) : !hasData ? (
          <div className="flex h-80 flex-col items-center justify-center space-y-2 text-center">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-200">No spending trends available</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Import transactions or categorise expenses to see spending trends.
            </p>
          </div>
        ) : (
          <>
            <div className="h-80 mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  data={chartData.map((point, idx) => {
                    const isProjected = point.isProjected === true;
                    const result: any = { ...point };
                    displayCategories.forEach((category) => {
                      const categoryKey = category.categoryId ?? 'uncategorised';
                      const actualDataKey = `${categoryKey}_actual`;
                      const projectedDataKey = `${categoryKey}_projected`;
                      // If this point is projected, use projected data key, otherwise use actual
                      if (isProjected) {
                        result[actualDataKey] = null;
                        result[projectedDataKey] = point[categoryKey] ?? null;
                      } else {
                        result[actualDataKey] = point[categoryKey] ?? null;
                        // Connect to next projected point if this is the last actual point
                        result[projectedDataKey] = (idx === projectedStartIndex - 1 && projectedStartIndex < chartData.length) ? point[categoryKey] ?? null : null;
                      }
                    });
                    return result;
                  })} 
                  margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" className="dark:stroke-gray-700" />
                  <XAxis
                    dataKey="period"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    className="dark:text-gray-400"
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    className="dark:text-gray-400"
                    tickFormatter={(value) => {
                      if (value >= 1000) {
                        return `$${(value / 1000).toFixed(1)}k`;
                      }
                      return `$${value}`;
                    }}
                  />
                  <Tooltip
                    content={
                      <TrendTooltip
                        formatter={formatCurrency}
                        topCategories={displayCategories}
                      />
                    }
                  />
                  {displayCategories.map((category, index) => {
                    const categoryKey = category.categoryId ?? 'uncategorised';
                    const color = category.categoryColor || FALLBACK_COLORS[index % FALLBACK_COLORS.length];

                    // Create separate data keys for actual and projected to render separately
                    const actualDataKey = `${categoryKey}_actual`;
                    const projectedDataKey = `${categoryKey}_projected`;

                    return (
                      <React.Fragment key={categoryKey}>
                        {/* Actual data line (solid) */}
                        <Line
                          type="monotone"
                          dataKey={actualDataKey}
                          name={category.categoryName}
                          stroke={color}
                          strokeWidth={2}
                          dot={(props: any) => {
                            const { cx, cy, payload, index: dotIndex } = props;
                            if (payload && !payload.isProjected && payload[actualDataKey] != null) {
                              return (
                                <circle
                                  key={`${categoryKey}-actual-${dotIndex}`}
                                  cx={cx}
                                  cy={cy}
                                  r={4}
                                  fill={color}
                                  stroke={color}
                                  strokeWidth={2}
                                />
                              );
                            }
                            // Return empty group instead of null to avoid key warnings
                            return <g key={`${categoryKey}-actual-${dotIndex}`} />;
                          }}
                          activeDot={{ r: 6 }}
                          connectNulls={false}
                        />
                        {/* Projected data line (dotted) */}
                        {projectedStartIndex < chartData.length && (
                          <Line
                            type="monotone"
                            dataKey={projectedDataKey}
                            name={`${category.categoryName} (Projected)`}
                            stroke={color}
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={(props: any) => {
                              const { cx, cy, payload, index: dotIndex } = props;
                              if (payload?.isProjected && payload[projectedDataKey] != null) {
                                return (
                                  <circle
                                    key={`${categoryKey}-projected-${dotIndex}`}
                                    cx={cx}
                                    cy={cy}
                                    r={4}
                                    fill={color}
                                    fillOpacity={0.6}
                                    stroke={color}
                                    strokeWidth={2}
                                  />
                                );
                              }
                              // Return empty group instead of null to avoid key warnings
                              return <g key={`${categoryKey}-projected-${dotIndex}`} />;
                            }}
                            activeDot={{ r: 6 }}
                            connectNulls={false}
                            isAnimationActive={false}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Category Checkboxes */}
            {categoryLimit === 'top5' ? (
              <div className="flex flex-wrap items-center justify-center gap-4">
                {filteredCategories.map((category, index) => {
                  const categoryId = category.categoryId ?? 'uncategorised';
                  const isSelected = selectedCategories.has(categoryId);
                  const color = category.categoryColor || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
                  const Icon = getCategoryIconComponent({
                    icon: category.categoryIcon,
                    name: category.categoryName,
                    type: CategoryType.EXPENSE,
                  });

                  return (
                    <label
                      key={categoryId}
                      className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleCategoryToggle(category.categoryId)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span
                        className="inline-flex h-3 w-3 flex-shrink-0 rounded-full"
                        style={{ backgroundColor: color }}
                        aria-hidden="true"
                      />
                      {Icon && (
                        <Icon className="h-3.5 w-3.5" style={{ color }} aria-hidden="true" />
                      )}
                      <span className="text-sm text-gray-600 dark:text-gray-300">{category.categoryName}</span>
                    </label>
                  );
                })}
              </div>
            ) : categoryLimit === 'top10' ? (
              <div className="grid grid-cols-2 gap-x-8 gap-y-3 justify-items-start">
                {filteredCategories.map((category, index) => {
                  const categoryId = category.categoryId ?? 'uncategorised';
                  const isSelected = selectedCategories.has(categoryId);
                  const color = category.categoryColor || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
                  const Icon = getCategoryIconComponent({
                    icon: category.categoryIcon,
                    name: category.categoryName,
                    type: CategoryType.EXPENSE,
                  });

                  return (
                    <label
                      key={categoryId}
                      className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleCategoryToggle(category.categoryId)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span
                        className="inline-flex h-3 w-3 flex-shrink-0 rounded-full"
                        style={{ backgroundColor: color }}
                        aria-hidden="true"
                      />
                      {Icon && (
                        <Icon className="h-3.5 w-3.5" style={{ color }} aria-hidden="true" />
                      )}
                      <span className="text-sm text-gray-600 dark:text-gray-300">{category.categoryName}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-x-6 gap-y-3 justify-items-start">
                {filteredCategories.map((category, index) => {
                  const categoryId = category.categoryId ?? 'uncategorised';
                  const isSelected = selectedCategories.has(categoryId);
                  const color = category.categoryColor || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
                  const Icon = getCategoryIconComponent({
                    icon: category.categoryIcon,
                    name: category.categoryName,
                    type: CategoryType.EXPENSE,
                  });

                  return (
                    <label
                      key={categoryId}
                      className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleCategoryToggle(category.categoryId)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span
                        className="inline-flex h-3 w-3 flex-shrink-0 rounded-full"
                        style={{ backgroundColor: color }}
                        aria-hidden="true"
                      />
                      {Icon && (
                        <Icon className="h-3.5 w-3.5" style={{ color }} aria-hidden="true" />
                      )}
                      <span className="text-sm text-gray-600 dark:text-gray-300">{category.categoryName}</span>
                    </label>
                  );
                })}
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
}

